import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  useConvertLead,
  useListInventoryPlans,
  useListSpaceResources,
  useAssignRecurringResource,
  useListInventoryPlanPeriods,
  getListInventoryPlanPeriodsQueryKey,
  useLookupInventoryPlanPeriod,
  useListPlanSecondaryAccess,
  getListPlanSecondaryAccessQueryKey,
  getListAccountsQueryKey,
  getListSpaceResourcesQueryKey,
} from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { useLocation } from "wouter";
import {
  AlertTriangle,
  ArrowLeft,
  Check,
  ChevronsUpDown,
  Monitor,
  Building2,
  Users,
  Presentation,
  CalendarDays,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useToast } from "@/hooks/use-toast";
import { uploadDocument } from "@/lib/upload";
import { lookupPincode } from "@/lib/india-pincode";
import { cn } from "@/lib/utils";

// 2026-06-10 UX rev: lifted out of the right-side Sheet onto a full
// page route (/accounts/convert/:leadId) — see pages/convert-lead.tsx.
// Filename retained as convert-lead-dialog.tsx to keep the diff narrow;
// the exported function is now ConvertLeadForm, not a Dialog.
//
// 2026-06-04 UX rev: sectioned (Personal / Plan & Billing / Are you a
// company? / Company details) · adds first+last name UI split + ID
// Type/Number + Aadhaar warning + company toggle.
const ID_TYPES = ["Aadhaar", "PAN", "Passport", "Driving_License", "Voter_ID"] as const;
const ID_TYPE_LABELS: Record<(typeof ID_TYPES)[number], string> = {
  Aadhaar: "Aadhaar",
  PAN: "PAN Card",
  Passport: "Passport",
  Driving_License: "Driving License",
  Voter_ID: "Voter ID",
};

const COMPANY_DOC_TYPES = ["company_registration", "gst_certificate", "gst_return"] as const;
const COMPANY_DOC_LABELS: Record<(typeof COMPANY_DOC_TYPES)[number], string> = {
  company_registration: "Company Registration",
  gst_certificate: "GST Certificate",
  gst_return: "GST Return",
};

const COMPANY_TYPES = [
  "Private_Limited",
  "Public_Limited",
  "LLP",
  "Partnership",
  "Sole_Proprietorship",
  "One_Person_Company",
  "Other",
] as const;
const COMPANY_TYPE_LABELS: Record<(typeof COMPANY_TYPES)[number], string> = {
  Private_Limited: "Private Limited",
  Public_Limited: "Public Limited",
  LLP: "LLP",
  Partnership: "Partnership",
  Sole_Proprietorship: "Sole Proprietorship",
  One_Person_Company: "One Person Company (OPC)",
  Other: "Other",
};

// Plan → expected space-resource type (Phase 21 vocabulary). Read directly
// from inventory_plans.resource_type (migration 0056). Returns null for
// plans with no space hold (Event, Shooting) and for the two room types
// (Meeting_Room / Conference_Room) which use the slot booking flow.
type SeatPickerType = "Workstation" | "Private_Cabin" | null;
function seatPickerTypeForPlan(
  resourceType: string | null | undefined,
): SeatPickerType {
  if (resourceType === "Workstation" || resourceType === "Private_Cabin") {
    return resourceType;
  }
  return null;
}

const ConvertLeadFormSchema = z.object({
  leadId: z.number(),
  // Personal
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().nullable().optional(),
  phone: z.string().min(1, "Mobile number is required"),
  email: z.string().nullable().optional(),
  idProofType: z.enum(ID_TYPES).nullable().optional(),
  idProofRef: z.string().nullable().optional(),
  // Account type toggle
  accountType: z.enum(["individual", "company"]),
  companyType: z.enum(COMPANY_TYPES).nullable().optional(),
  // Company details (only used when accountType=company)
  legalName: z.string().min(1, "Legal name is required"),
  gstTreatment: z.enum(["registered_b2b", "unregistered_b2c"]),
  gstin: z.string().nullable().optional(),
  placeOfSupply: z.string().min(1, "Place of supply is required"),
  billingAddress: z.string().nullable().optional(),
  pincode: z.string().nullable().optional(),
  city: z.string().nullable().optional(),
  state: z.string().nullable().optional(),
  // 2026-06-10 — Plan & Billing dropped from the convert form. The
  // operator registers the account first; plan attaches later from
  // /accounts/:id Plan tab. Fields kept optional so any callers passing
  // them through don't trip Zod, but the form UI doesn't ask anymore.
  planId: z.number().int().min(1).optional(),
  startDate: z.string().optional(),
  billingCycleDay: z.number().min(1).max(28).nullable().optional(),
  cycleMode: z.enum(["Monthly", "Quarterly", "Six_Month", "Yearly"]).optional(),
  discountPct: z.number().min(0).max(100).optional(),
});

type ConvertLeadFormValues = z.infer<typeof ConvertLeadFormSchema>;

interface Props {
  lead: {
    id: number;
    customerName: string;
    mobileNumber: string;
    email: string | null;
  };
  /** Where to send the operator on cancel/success. Defaults to /accounts. */
  backHref?: string;
}

// Split "First Last [Middle]" → { first, last }.
// If only one token: first = it, last = "".
function splitName(full: string): { first: string; last: string } {
  const trimmed = full.trim();
  if (!trimmed) return { first: "", last: "" };
  const parts = trimmed.split(/\s+/);
  if (parts.length === 1) return { first: parts[0]!, last: "" };
  return { first: parts[0]!, last: parts.slice(1).join(" ") };
}

/**
 * Phase 27 — small local debounce helper so the matrix-lookup query doesn't
 * fire on every keystroke of the Seats input. 250ms matches RESEARCH Pitfall 6.
 */
function useDebouncedValue<T>(value: T, delayMs = 250): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(id);
  }, [value, delayMs]);
  return debounced;
}

/**
 * 2026-06-10 — Plan icon by declared resourceType. Plans with no resource
 * (Day Pass / Event / Shooting) get a generic calendar icon.
 */
function PlanIcon({ resourceType }: { resourceType: string | null | undefined }) {
  const className = "h-3.5 w-3.5 shrink-0 text-muted-foreground";
  switch (resourceType) {
    case "Workstation":
      return <Monitor className={className} aria-label="Workstation" />;
    case "Private_Cabin":
      return <Building2 className={className} aria-label="Private Cabin" />;
    case "Meeting_Room":
      return <Users className={className} aria-label="Meeting Room" />;
    case "Conference_Room":
      return <Presentation className={className} aria-label="Conference Room" />;
    default:
      return <CalendarDays className={className} aria-label="No fixed resource" />;
  }
}

/** Phase 27 — INR formatter (en-IN locale, no decimals). */
function formatRupee(amount: number): string {
  if (!Number.isFinite(amount) || amount <= 0) return "—";
  return new Intl.NumberFormat("en-IN", {
    maximumFractionDigits: 0,
  }).format(amount);
}

export function ConvertLeadForm({ lead, backHref = "/accounts" }: Props) {
  const { toast } = useToast();
  const [, navigate] = useLocation();
  const queryClient = useQueryClient();
  const plans = useListInventoryPlans();
  const convert = useConvertLead();
  const assignRecurring = useAssignRecurringResource();
  const [planPickerOpen, setPlanPickerOpen] = useState(false);
  // Multi-seat: store as a Set of resource ids so company accounts can claim
  // several seats / cabins in one go. Individual accounts typically pick 1.
  const [pickedResourceIds, setPickedResourceIds] = useState<number[]>([]);

  // Company-doc upload is held outside react-hook-form (File can't be Zod-parsed
  // and we don't POST it in the convert body — uploads run after the account is
  // created against POST /accounts/{id}/documents).
  const [companyDocType, setCompanyDocType] = useState<(typeof COMPANY_DOC_TYPES)[number] | "">("");
  const [companyDocFile, setCompanyDocFile] = useState<File | null>(null);
  const [companyRegNumber, setCompanyRegNumber] = useState("");
  const [personalIdFile, setPersonalIdFile] = useState<File | null>(null);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [pincodeLookupStatus, setPincodeLookupStatus] = useState<"idle" | "loading" | "ok" | "not_found" | "error">("idle");

  const initialNames = splitName(lead.customerName);

  const form = useForm<ConvertLeadFormValues>({
    resolver: zodResolver(ConvertLeadFormSchema),
    defaultValues: {
      leadId: lead.id,
      firstName: initialNames.first,
      lastName: initialNames.last,
      phone: lead.mobileNumber,
      email: lead.email ?? "",
      idProofType: null,
      idProofRef: "",
      accountType: "individual",
      companyType: null,
      legalName: lead.customerName,
      gstTreatment: "unregistered_b2c",
      gstin: null,
      placeOfSupply: "07",
      billingAddress: "",
      pincode: "",
      city: "",
      state: "",
      planId: undefined as unknown as number,
      startDate: new Date().toISOString().slice(0, 10),
      billingCycleDay: 1,
      // cycleMode is derived server-side from the plan's billing cycle.
      discountPct: 0,
    },
  });

  const watchedPlanId = form.watch("planId");
  const watchedGstTreatment = form.watch("gstTreatment");
  const watchedAccountType = form.watch("accountType");
  const watchedIdProofType = form.watch("idProofType");
  const watchedFirst = form.watch("firstName");
  const watchedLast = form.watch("lastName");
  const watchedPhone = form.watch("phone");
  const watchedLegalName = form.watch("legalName");
  const watchedCompanyType = form.watch("companyType");
  const watchedPlaceOfSupply = form.watch("placeOfSupply");
  const watchedPincode = form.watch("pincode");

  // Controlled accordion: gate Plan & Billing on Personal + Company completeness.
  const [openSections, setOpenSections] = useState<string[]>(["personal"]);

  const activePlans = (plans.data ?? []).filter((p) => p.status === "Active");
  const selectedPlan = activePlans.find((p) => p.id === watchedPlanId) ?? null;
  const isRecurring = selectedPlan?.cadence === "Recurring";
  const isB2B = watchedGstTreatment === "registered_b2b";
  const isCompany = watchedAccountType === "company";
  const showAadhaarWarning = watchedIdProofType === "Aadhaar";

  // Personal Details completeness: required fields are First Name + Mobile Number.
  // ID Type/Number + ID upload are optional (can be added post-convert from /accounts/{id}).
  const personalComplete = !!(watchedFirst?.trim() && watchedPhone?.trim());

  // Company Details completeness (only relevant when toggle is on): legal name,
  // 2026-06-10 — Upload File on Company Document is OPTIONAL: operators
  // typically receive the signed agreement AFTER the proof is processed,
  // so we don't block conversion on it. Doc Type + Registration Number
  // are still required because they're cheap data the operator has at
  // conversion time.
  const companyComplete = !isCompany || !!(
    watchedLegalName?.trim() &&
    watchedCompanyType &&
    watchedPlaceOfSupply?.trim() &&
    (watchedGstTreatment !== "registered_b2b" || (form.watch("gstin") ?? "").trim()) &&
    companyDocType && companyRegNumber.trim()
  );

  // Plan & Billing is editable only when Personal + Company are both complete.
  const canEditPlan = personalComplete && companyComplete;

  // Phase 24-06: period override discounts + cross-plan secondary access for callouts.
  // Phase 26: signature is now (id, params?, options?) — pass undefined for params.
  const { data: planPeriods = [] } = useListInventoryPlanPeriods(watchedPlanId ?? 0, undefined, {
    query: {
      enabled: !!watchedPlanId,
      queryKey: getListInventoryPlanPeriodsQueryKey(watchedPlanId ?? 0),
    },
  });
  const { data: planSecondaryAccess = [] } = useListPlanSecondaryAccess(watchedPlanId ?? 0, {
    query: {
      enabled: !!watchedPlanId,
      queryKey: getListPlanSecondaryAccessQueryKey(watchedPlanId ?? 0),
    },
  });

  // ── Phase 27 — matrix-aware (seats × period) tier preview ───────────────
  //
  // Operator picks a Seats count; we auto-derive the period from the plan
  // (Conference_Room → 1_Hour, Recurring → 1_Month, Non_Recurring → validityDays_Day —
  // matches resolveMatrixBinding cycle derivation in matrix-lookup.ts).
  //
  // When the (planId, period, seats) tuple matches an Active matrix row on
  // the server, we render a "Tier" badge showing the frozen-discounted price
  // operator preview. On miss / 404 we silently fall back (no badge) — the
  // legacy quantity × monthlyPrice display stays the source of truth UX-side.
  //
  // The server still owns the authoritative freeze-on-create (Plan 27-03).
  // V1 has no explicit period override picker — deferred to Phase 28.
  const [seats, setSeats] = useState(1);
  const debouncedSeats = useDebouncedValue(seats, 250);

  const derivedPeriod = useMemo<
    | { value: number; unit: "Hour" | "Day" | "Month" }
    | null
  >(() => {
    if (!selectedPlan) return null;
    if (selectedPlan.resourceType === "Conference_Room") {
      return { value: 1, unit: "Hour" };
    }
    if (selectedPlan.cadence === "Recurring") {
      return { value: 1, unit: "Month" };
    }
    const validityDays =
      typeof selectedPlan.validityDays === "number" && selectedPlan.validityDays > 0
        ? selectedPlan.validityDays
        : 1;
    return { value: validityDays, unit: "Day" };
  }, [selectedPlan]);

  const lookupEnabled =
    !!watchedPlanId && !!derivedPeriod && debouncedSeats >= 1;

  const { data: matrixRow } = useLookupInventoryPlanPeriod(
    watchedPlanId ?? 0,
    {
      periodValue: derivedPeriod?.value ?? 1,
      periodUnit: (derivedPeriod?.unit ?? "Month") as "Hour" | "Day" | "Month",
      seats: debouncedSeats,
    },
    {
      query: {
        enabled: lookupEnabled,
        queryKey: [
          "lookupInventoryPlanPeriod",
          watchedPlanId,
          derivedPeriod?.value,
          derivedPeriod?.unit,
          debouncedSeats,
        ],
        // 404 = no matching matrix row (silent fallback). React Query's
        // retry would otherwise hammer the endpoint while operator types.
        retry: false,
      },
    },
  );

  // Build tier badge copy when match returns. Computed display only — server
  // freezes the canonical value at membership creation.
  const tierBadgeCopy = useMemo(() => {
    if (!matrixRow || !selectedPlan || !derivedPeriod) return null;
    const monthlyPrice = Number(selectedPlan.monthlyPrice ?? 0);
    const discountPct = Number(matrixRow.discountPct ?? 0);
    if (!Number.isFinite(monthlyPrice) || monthlyPrice <= 0) return null;
    const baseline = monthlyPrice * debouncedSeats * derivedPeriod.value;
    const discounted = baseline * (1 - discountPct / 100);
    return {
      seats: debouncedSeats,
      periodLabel: `${derivedPeriod.value}_${derivedPeriod.unit}`,
      discounted: formatRupee(discounted),
      baseline: formatRupee(baseline),
      discountPct: discountPct,
    };
  }, [matrixRow, selectedPlan, derivedPeriod, debouncedSeats]);

  // Derived: registration readiness for warning banners.
  const watchedGstin = form.watch("gstin");
  const accountIsB2BReady =
    watchedGstTreatment === "registered_b2b" && !!(watchedGstin ?? "").trim();
  const accountHasGstin = !!(watchedGstin ?? "").trim();

  // Phase 21 ↔ Phase 23 structural link (migration 0056): read directly from
  // the plan's declared resource type instead of guessing from the name.
  const seatPickerType: SeatPickerType = seatPickerTypeForPlan(selectedPlan?.resourceType ?? null);
  const resourcesQuery = useListSpaceResources(
    seatPickerType ? { resourceType: seatPickerType } : undefined,
    {
      query: {
        enabled: seatPickerType != null,
        queryKey: getListSpaceResourcesQueryKey(
          seatPickerType ? { resourceType: seatPickerType } : undefined,
        ),
      },
    },
  );
  const availableResources = (resourcesQuery.data ?? []).filter((r) => r.status === "Active");
  const pickedResources = availableResources.filter((r) => pickedResourceIds.includes(r.id));

  // 2026-06-10 — fetch ALL space resources (no filter) so the Plan picker can
  // show a per-plan availability count next to the plan name. Distinct from
  // resourcesQuery above (which is gated to the selected plan's resourceType).
  // One call covers all 4 resource types we care about.
  const allResourcesQuery = useListSpaceResources(undefined, {
    query: {
      queryKey: getListSpaceResourcesQueryKey(undefined),
    },
  });
  const resourceCountByType = useMemo<Record<string, number>>(() => {
    const counts: Record<string, number> = {};
    for (const r of allResourcesQuery.data ?? []) {
      if (r.status !== "Active") continue;
      counts[r.resourceType] = (counts[r.resourceType] ?? 0) + 1;
    }
    return counts;
  }, [allResourcesQuery.data]);

  // For individual accounts the legal_name mirrors the contact's display name
  // (first + last). For company accounts the operator types the company name.
  // legalName mirrors First+Last ONLY for individual accounts.
  // For company accounts, the operator types the legal entity name —
  // it stays blank until they fill it in.
  useEffect(() => {
    if (!isCompany) {
      const composed = `${watchedFirst ?? ""} ${watchedLast ?? ""}`.trim();
      if (composed) form.setValue("legalName", composed);
    }
  }, [watchedFirst, watchedLast, isCompany, form]);

  // When the operator flips the "Are you a company?" toggle from off → on,
  // CLEAR the legalName field so they don't have a half-correct "John Doe"
  // sitting there as the company name. The toggle-off case (handled in the
  // Switch onCheckedChange) restores the name-derived value.
  const [wasCompany, setWasCompany] = useState(false);
  useEffect(() => {
    if (isCompany && !wasCompany) {
      form.setValue("legalName", "");
    }
    setWasCompany(isCompany);
  }, [isCompany, wasCompany, form]);

  // Section auto-open behavior:
  // - When Personal Details is complete AND the company toggle is on, auto-open Company Details.
  // - When canEditPlan becomes true, auto-open Plan & Billing (and collapse Personal/Company so
  //   the operator sees the unlocked section without scrolling).
  // - When the toggle flips off, drop Company from the open set.
  useEffect(() => {
    if (personalComplete && isCompany) {
      setOpenSections((prev) => (prev.includes("company") ? prev : [...prev, "company"]));
    }
    if (!isCompany) {
      setOpenSections((prev) => prev.filter((v) => v !== "company"));
    }
  }, [personalComplete, isCompany]);

  useEffect(() => {
    if (canEditPlan) {
      setOpenSections((prev) => (prev.includes("plan") ? prev : [...prev, "plan"]));
    } else {
      // Force Plan closed when prerequisites are not met.
      setOpenSections((prev) => prev.filter((v) => v !== "plan"));
    }
  }, [canEditPlan]);

  // Controlled onValueChange: silently strip "plan" if the operator tries to
  // open it before completing prerequisites.
  function handleAccordionChange(values: string[]) {
    if (!canEditPlan && values.includes("plan")) {
      values = values.filter((v) => v !== "plan");
    }
    setOpenSections(values);
  }

  // Pincode → city/state/place_of_supply autofetch via India Post API.
  // Debounced 400ms; only fires when pincode is 6 digits + company toggle is on.
  useEffect(() => {
    if (!isCompany) {
      setPincodeLookupStatus("idle");
      return;
    }
    const pin = (watchedPincode ?? "").trim();
    if (!/^\d{6}$/.test(pin)) {
      setPincodeLookupStatus("idle");
      return;
    }
    let cancelled = false;
    setPincodeLookupStatus("loading");
    const t = setTimeout(async () => {
      const result = await lookupPincode(pin);
      if (cancelled) return;
      if (!result.ok) {
        setPincodeLookupStatus(result.reason === "not_found" ? "not_found" : "error");
        return;
      }
      form.setValue("city", result.city, { shouldDirty: true });
      form.setValue("state", result.state, { shouldDirty: true });
      if (result.stateCode) {
        form.setValue("placeOfSupply", result.stateCode, { shouldDirty: true });
      }
      setPincodeLookupStatus("ok");
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [watchedPincode, isCompany, form]);

  function onPlanChange(id: number) {
    form.setValue("planId", id);
    const next = activePlans.find((p) => p.id === id);
    if (next?.cadence === "Non_Recurring") {
      form.setValue("billingCycleDay", null);
    } else {
      form.setValue("billingCycleDay", form.getValues("billingCycleDay") ?? 1);
    }
    setPickedResourceIds([]); // clear seat selection on plan change
    setSeats(1); // Phase 27: reset seats on plan change so matrix lookup re-fires cleanly
    setPlanPickerOpen(false);
  }

  async function onSubmit(values: ConvertLeadFormValues) {
    // 2026-06-10 — Plan is attached LATER from /accounts/:id Plan tab.
    // The plan-gate that used to live here is gone.
    // Company accounts: enforce mandatory doc fields + companyType at the form
    // level (Zod doesn't see the doc state since the file isn't in the form).
    if (values.accountType === "company") {
      if (!values.companyType) {
        toast({ title: "Pick a Company Type.", variant: "destructive" });
        return;
      }
      // Upload File is OPTIONAL — operator can attach the agreement
      // later once the proof is processed. Doc Type + Registration
      // Number still required.
      if (!companyDocType || !companyRegNumber.trim()) {
        toast({
          title: "Company document details missing.",
          description: "Pick a doc type and enter the registration number. File upload is optional.",
          variant: "destructive",
        });
        return;
      }
    }
    const fullName = `${values.firstName} ${values.lastName ?? ""}`.trim();
    try {
      // 2026-06-10 — Plan attached later. Convert payload sends only the
      // account/contact fields; planId/startDate/billingCycleDay/cycleMode/
      // seats are omitted. Server's ConvertLeadBody allows planId nullable
      // and skips membership insert when absent.
      const result = await convert.mutateAsync({
        data: {
          legalName: values.legalName,
          accountType: values.accountType,
          companyType: values.accountType === "company" ? (values.companyType ?? null) : null,
          gstTreatment: values.gstTreatment,
          gstin: isB2B ? (values.gstin || null) : null,
          placeOfSupply: values.placeOfSupply,
          billingAddress: values.billingAddress || null,
          pincode: values.pincode || null,
          city: values.city || null,
          state: values.state || null,
          fullName,
          phone: values.phone,
          email: values.email || null,
          idProofType: values.idProofType ?? null,
          idProofRef: values.idProofRef || null,
        },
      });
      toast({
        title: `Account created for ${values.legalName}.`,
        description: "Attach a plan from the Plan tab on the account page.",
      });

      // Optional second step(s): upload personal ID image (kind=kyc, attached
      // to primary contact) and/or company doc (3 kinds, account-level). Both
      // run after the account/contact/membership commit; a failure here does
      // NOT roll back the account — the operator can re-upload from the
      // Documents tab.
      const willUploadKyc = Boolean(personalIdFile && values.idProofType);
      const willUploadCompanyDoc = Boolean(isCompany && companyDocFile && companyDocType);
      if (willUploadKyc || willUploadCompanyDoc) {
        setUploadingDoc(true);
        try {
          if (willUploadKyc && personalIdFile && values.idProofType) {
            // Prefix filename with the ID type + masked-ish ref so it's
            // searchable in Supabase Storage.
            const refTag = (values.idProofRef ?? "").trim().replace(/[^a-zA-Z0-9-_]/g, "");
            const renamed = new File(
              [personalIdFile],
              `${values.idProofType}${refTag ? `-${refTag}` : ""}-${personalIdFile.name}`,
              { type: personalIdFile.type },
            );
            await uploadDocument(result.accountId, "kyc", renamed, {
              contactId: result.primaryContactId,
            });
            toast({ title: `Uploaded ${ID_TYPE_LABELS[values.idProofType]} proof.` });
          }

          if (willUploadCompanyDoc && companyDocFile && companyDocType) {
            const renamed = companyRegNumber.trim()
              ? new File(
                  [companyDocFile],
                  `RegNo-${companyRegNumber.trim().replace(/[^a-zA-Z0-9-_]/g, "")}-${companyDocFile.name}`,
                  { type: companyDocFile.type },
                )
              : companyDocFile;
            await uploadDocument(result.accountId, companyDocType, renamed);
            toast({ title: `Uploaded ${COMPANY_DOC_LABELS[companyDocType]}.` });
          }
        } catch (err) {
          // Surface the actual error so the operator (and dev tools) see
          // why the upload failed instead of a generic message. The
          // account itself is already committed; the operator can
          // re-upload from the Documents tab once the issue is fixed.
          const detail = (err as Error)?.message ?? "Unknown error";
          // eslint-disable-next-line no-console
          console.error("[convert-lead] document upload failed:", err);
          toast({
            title: "Document upload failed.",
            description: `${detail}. Account is created; re-upload from the Documents tab once the issue is fixed.`,
            variant: "destructive",
          });
        } finally {
          setUploadingDoc(false);
        }
      }

      queryClient.invalidateQueries({ queryKey: getListAccountsQueryKey() });
      form.reset();
      setCompanyDocFile(null);
      setCompanyDocType("");
      setCompanyRegNumber("");
      setPersonalIdFile(null);
      setPickedResourceIds([]);
      setSeats(1);
      navigate(`/accounts/${result.accountId}`);
    } catch {
      toast({ title: "Something went wrong. Please try again.", variant: "destructive" });
    }
  }

  return (
    <div className="mx-auto w-full max-w-3xl space-y-4 p-4 sm:p-6">
      <div className="flex items-center gap-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => navigate(backHref)}
          data-testid="convert-lead-back"
        >
          <ArrowLeft className="mr-1 h-4 w-4" />
          Back
        </Button>
      </div>
      <div className="space-y-1">
        <h1 className="text-2xl font-semibold tracking-tight">
          Convert {lead.customerName} to Account
        </h1>
        <p className="text-sm text-muted-foreground">
            These details are copied from the lead. Editing here does not change the original lead.
        </p>
      </div>

      <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 pt-4">

            <Accordion
              type="multiple"
              value={openSections}
              onValueChange={handleAccordionChange}
              className="flex flex-col gap-2"
            >

            {/* ============== Personal Details ============== */}
            <AccordionItem value="personal" className="order-1 border rounded-md px-3">
              <AccordionTrigger className="hover:no-underline py-3">
                <span className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                  Personal Details
                </span>
              </AccordionTrigger>
              <AccordionContent className="space-y-3 pt-1 pb-3">

              <div className="grid grid-cols-2 gap-3">
                <FormField name="firstName" control={form.control} render={({ field }) => (
                  <FormItem>
                    <FormLabel>First Name <span className="text-destructive">*</span></FormLabel>
                    <FormControl><Input {...field} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )} />

                <FormField name="lastName" control={form.control} render={({ field }) => (
                  <FormItem>
                    <FormLabel>Last Name</FormLabel>
                    <FormControl><Input {...field} value={field.value ?? ""} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
              </div>

              <FormField name="phone" control={form.control} render={({ field }) => (
                <FormItem>
                  <FormLabel>Mobile Number <span className="text-destructive">*</span></FormLabel>
                  <FormControl><Input type="tel" {...field} /></FormControl>
                  <FormMessage />
                </FormItem>
              )} />

              <FormField name="email" control={form.control} render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl><Input type="email" {...field} value={field.value ?? ""} /></FormControl>
                  <FormMessage />
                </FormItem>
              )} />

              <div className="grid grid-cols-2 gap-3">
                <FormField name="idProofType" control={form.control} render={({ field }) => (
                  <FormItem>
                    <FormLabel>Personal ID Type</FormLabel>
                    <Select
                      onValueChange={(v) => field.onChange(v as typeof ID_TYPES[number])}
                      value={field.value ?? undefined}
                    >
                      <FormControl><SelectTrigger><SelectValue placeholder="Choose…" /></SelectTrigger></FormControl>
                      <SelectContent>
                        {ID_TYPES.map((t) => (
                          <SelectItem key={t} value={t}>{ID_TYPE_LABELS[t]}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )} />

                <FormField name="idProofRef" control={form.control} render={({ field }) => (
                  <FormItem>
                    <FormLabel>ID Number</FormLabel>
                    <FormControl><Input {...field} value={field.value ?? ""} placeholder="e.g. ABCDE1234F" /></FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
              </div>

              {showAadhaarWarning && (
                <Alert variant="default" className="border-amber-300 bg-amber-50 text-amber-900">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription className="text-xs">
                    Aadhaar contains sensitive PII. Only capture it if there is a specific legal obligation; otherwise prefer PAN or Driving License.
                  </AlertDescription>
                </Alert>
              )}

              <div className="space-y-1 rounded-md border bg-muted/30 p-3">
                <label className="text-xs font-medium">Upload Personal ID Proof (optional)</label>
                <Input
                  type="file"
                  accept="application/pdf,image/*"
                  onChange={(e) => setPersonalIdFile(e.target.files?.[0] ?? null)}
                  disabled={!watchedIdProofType}
                />
                <p className="text-[10px] text-muted-foreground">
                  {watchedIdProofType
                    ? `Stored under contacts/{id}/kyc/ as type=kyc. Filename prefixed with ${ID_TYPE_LABELS[watchedIdProofType]}${form.watch("idProofRef") ? ` — ${form.watch("idProofRef")}` : ""}.`
                    : "Pick a Personal ID Type above first to enable upload."}
                </p>
              </div>

              {/* === "Are you a company?" toggle lives inside Personal Details === */}
              <FormField name="accountType" control={form.control} render={({ field }) => (
                <FormItem className="flex items-center justify-between gap-4 rounded-md border p-3">
                  <div className="space-y-0.5">
                    <FormLabel className="text-sm">Are you a company?</FormLabel>
                    <p className="text-xs text-muted-foreground">
                      Toggle on to bill a company entity (B2B GSTIN, separate legal name).
                    </p>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value === "company"}
                      onCheckedChange={(checked) => {
                        field.onChange(checked ? "company" : "individual");
                        if (!checked) {
                          // back to individual: reset company-shaped fields, restore name-derived legal name
                          form.setValue("gstTreatment", "unregistered_b2c");
                          form.setValue("gstin", null);
                          const composed = `${watchedFirst ?? ""} ${watchedLast ?? ""}`.trim();
                          if (composed) form.setValue("legalName", composed);
                        }
                      }}
                    />
                  </FormControl>
                </FormItem>
              )} />
              </AccordionContent>
            </AccordionItem>

            {/* Plan & Billing section removed 2026-06-10 — operator attaches plan from /accounts/:id Plan tab after account creation. */}


            {/* ============== Company Details (mounts when toggle is on; auto-expands when Personal is complete) ============== */}
            {isCompany && (
              <AccordionItem value="company" className="order-2 border rounded-md px-3">
                <AccordionTrigger className="hover:no-underline py-3">
                  <span className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                    Company Details
                  </span>
                </AccordionTrigger>
                <AccordionContent className="space-y-3 pt-1 pb-3">

                <FormField name="legalName" control={form.control} render={({ field }) => (
                  <FormItem>
                    <FormLabel>Company Legal Name <span className="text-destructive">*</span></FormLabel>
                    <FormControl><Input {...field} placeholder="e.g. Maveltec Solutions Pvt. Ltd." /></FormControl>
                    <FormMessage />
                  </FormItem>
                )} />

                <FormField name="companyType" control={form.control} render={({ field }) => (
                  <FormItem>
                    <FormLabel>Company Type <span className="text-destructive">*</span></FormLabel>
                    <Select
                      onValueChange={(v) => field.onChange(v as typeof COMPANY_TYPES[number])}
                      value={field.value ?? undefined}
                    >
                      <FormControl><SelectTrigger><SelectValue placeholder="Choose…" /></SelectTrigger></FormControl>
                      <SelectContent>
                        {COMPANY_TYPES.map((t) => (
                          <SelectItem key={t} value={t}>{COMPANY_TYPE_LABELS[t]}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )} />

                <FormField name="gstTreatment" control={form.control} render={({ field }) => (
                  <FormItem>
                    <FormLabel>GST Treatment</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl><SelectTrigger><SelectValue /></SelectTrigger></FormControl>
                      <SelectContent>
                        <SelectItem value="registered_b2b">GST Registered</SelectItem>
                        <SelectItem value="unregistered_b2c">Not Registered</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )} />

                {isB2B && (
                  <FormField name="gstin" control={form.control} render={({ field }) => (
                    <FormItem>
                      <FormLabel>GSTIN <span className="text-destructive">*</span></FormLabel>
                      <FormControl><Input {...field} value={field.value ?? ""} placeholder="22AAAAA0000A1Z5" /></FormControl>
                      <FormMessage />
                    </FormItem>
                  )} />
                )}

                <FormField name="billingAddress" control={form.control} render={({ field }) => (
                  <FormItem>
                    <FormLabel>Company Address</FormLabel>
                    <FormControl>
                      <Input {...field} value={field.value ?? ""} placeholder="Street, Area" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )} />

                <div className="grid grid-cols-3 gap-3">
                  <FormField name="pincode" control={form.control} render={({ field }) => (
                    <FormItem>
                      <FormLabel>Pincode</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          value={field.value ?? ""}
                          inputMode="numeric"
                          maxLength={6}
                          placeholder="110078"
                        />
                      </FormControl>
                      <p className="text-[10px] text-muted-foreground">
                        {pincodeLookupStatus === "loading" && "Looking up city + state…"}
                        {pincodeLookupStatus === "ok" && "✓ City + state auto-filled"}
                        {pincodeLookupStatus === "not_found" && "Pincode not recognized — fill manually"}
                        {pincodeLookupStatus === "error" && "Lookup failed — fill manually"}
                        {pincodeLookupStatus === "idle" && "Enter 6 digits to auto-fill city + state"}
                      </p>
                      <FormMessage />
                    </FormItem>
                  )} />

                  <FormField name="city" control={form.control} render={({ field }) => (
                    <FormItem>
                      <FormLabel>City</FormLabel>
                      <FormControl><Input {...field} value={field.value ?? ""} placeholder="Najafgarh" /></FormControl>
                      <FormMessage />
                    </FormItem>
                  )} />

                  <FormField name="state" control={form.control} render={({ field }) => (
                    <FormItem>
                      <FormLabel>State</FormLabel>
                      <FormControl><Input {...field} value={field.value ?? ""} placeholder="Delhi" /></FormControl>
                      <FormMessage />
                    </FormItem>
                  )} />
                </div>

                <FormField name="placeOfSupply" control={form.control} render={({ field }) => (
                  <FormItem>
                    <FormLabel>Place of Supply <span className="text-destructive">*</span></FormLabel>
                    <FormControl><Input {...field} placeholder="07" maxLength={2} /></FormControl>
                    <p className="text-[10px] text-muted-foreground">
                      2-digit GST state code (auto-filled from pincode lookup; override only if the legal billing state differs)
                    </p>
                    <FormMessage />
                  </FormItem>
                )} />

                {/* Company document upload — required for company accounts. */}
                <div className="space-y-2 rounded-md border bg-muted/30 p-3">
                  <p className="text-xs font-medium">
                    Company Document <span className="text-destructive">*</span>
                  </p>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <label className="text-xs font-medium">
                        Document Type <span className="text-destructive">*</span>
                      </label>
                      <Select
                        value={companyDocType}
                        onValueChange={(v) => setCompanyDocType(v as typeof COMPANY_DOC_TYPES[number])}
                      >
                        <SelectTrigger><SelectValue placeholder="Choose…" /></SelectTrigger>
                        <SelectContent>
                          {COMPANY_DOC_TYPES.map((t) => (
                            <SelectItem key={t} value={t}>{COMPANY_DOC_LABELS[t]}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-1">
                      <label className="text-xs font-medium">
                        Registration Number <span className="text-destructive">*</span>
                      </label>
                      <Input
                        value={companyRegNumber}
                        onChange={(e) => setCompanyRegNumber(e.target.value)}
                        placeholder="e.g. U72200DL2020PTC123456"
                      />
                    </div>
                  </div>

                  <div className="space-y-1">
                    <label className="text-xs font-medium">
                      Upload File <span className="text-muted-foreground font-normal">(optional)</span>
                    </label>
                    <Input
                      type="file"
                      accept="application/pdf,image/*"
                      onChange={(e) => setCompanyDocFile(e.target.files?.[0] ?? null)}
                    />
                    <p className="text-[10px] text-muted-foreground">
                      PDF or image. Stored in Supabase Storage under <code>accounts/{`{id}`}/documents/</code>.
                      Registration number is prepended to the filename for searchability (a structured column lands in Phase 24).
                    </p>
                  </div>
                </div>
                </AccordionContent>
              </AccordionItem>
            )}

            </Accordion>

            {/* ============== Footer ============== */}
            <div className="flex justify-end gap-2 border-t pt-4">
              <Button type="button" variant="outline" onClick={() => navigate(backHref)}>
                Discard
              </Button>
              <Button type="submit" disabled={convert.isPending || uploadingDoc}>
                {convert.isPending
                  ? "Creating account…"
                  : uploadingDoc
                  ? "Uploading document…"
                  : "Convert Lead to Account"}
              </Button>
            </div>
          </form>
        </Form>
    </div>
  );
}
